import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles, RefreshCw } from 'lucide-react';
import api from '../../api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';

const fmt = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

export const AutoTimeLog = ({ isOwner }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const load = useCallback(() => api.get('/auto-time', { params: { days: 30 } }).then((r) => setData(r.data)).catch(() => setData({ entries: [], totalHours: 0 })), []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => { setBusy(true); try { await api.post('/auto-time/run'); await load(); } catch (err) { toast({ title: 'Could not fill missing days', description: err?.response?.data?.detail || 'Please try again', variant: 'destructive' }); } finally { setBusy(false); } };

  if (!data) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="auto-time-log">
      <div className="px-5 py-4 border-b border-stone-200 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="font-medium text-stone-900 flex items-center gap-2"><Sparkles size={16} className="text-emerald-700"/> Auto-logged retainer time</div>
          <p className="text-xs text-stone-500 mt-0.5">On weekdays with nothing posted, retainer clients get 45 min – 1h30 logged automatically. Not billable — counts towards effort &amp; portal stats only.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-600" data-testid="auto-time-total">Last 30 days: <b>{data.totalHours.toFixed(2)} h</b></span>
          {isOwner && <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="auto-time-run-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <RefreshCw size={14} className="mr-1"/>} Fill missing days
          </Button>}
        </div>
      </div>
      {data.entries.length === 0 ? <div className="p-8 text-center text-sm text-stone-500" data-testid="auto-time-empty">No auto time logged yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide"><tr>
              <th className="text-left px-5 py-2">Date</th><th className="text-left px-5 py-2">Client</th><th className="text-left px-5 py-2">Description</th><th className="text-right px-5 py-2">Time</th>
            </tr></thead>
            <tbody>{data.entries.map((e) => (
              <tr key={e.id} className="border-t border-stone-100" data-testid={`auto-time-row-${e.id}`}>
                <td className="px-5 py-2.5 whitespace-nowrap">{new Date(`${e.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                <td className="px-5 py-2.5">{e.clientName}</td>
                <td className="px-5 py-2.5 text-stone-600">{e.description} <Badge className="ml-2 h-5 px-1.5 text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`auto-badge-${e.id}`}>Auto</Badge></td>
                <td className="px-5 py-2.5 text-right tabular-nums">{fmt(e.minutes)}</td>
              </tr>))}
            </tbody>
          </table>
        </div>)}
    </div>
  );
};
